import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleNumber } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useDeleteCommissionsSettingsRanks,
  useFilterCommissionsSettingsRanks,
} from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import type { UUID } from "../../../../../typings/global";
import { useHasPermissions } from "../../../../auth/hooks";
import { CommissionsSettingsRank } from "../../types";

type Props = {
  onUpdateRank: (rank: CommissionsSettingsRank) => void;
};

export const CommissionsSettingsRanksTableView = ({ onUpdateRank }: Props) => {
  const { t } = useTranslation();

  const hasTopAgentCommissionsManagePermission = useHasPermissions(Permission.TOP_AGENT_COMMISSIONS_MANAGE);

  const navigate = useNavigate();
  const searchParams = useFilterSearchParams();

  const { data, isLoading } = useFilterCommissionsSettingsRanks(searchParams);
  const deleteCommissionsSettingsRanks = useDeleteCommissionsSettingsRanks();

  const handleDeleteRank = async (id: UUID) => {
    await deleteCommissionsSettingsRanks.mutateAsync(id);
  };

  const getColumns = () => {
    const columns: ColumnsType<CommissionsSettingsRank> = [
      {
        key: "orderIndex",
        title: t("commissions.rank.attrs.orderIndex"),
        width: 55,
        render: (_, record) => formatLocaleNumber(record.orderIndex),
      },
      {
        key: "name",
        title: t("commissions.rank.attrs.name"),
        ellipsis: { showTitle: false },
        width: 255,
        render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
      },
      {
        key: "nextRank",
        title: t("commissions.rank.attrs.nextRank"),
        ellipsis: { showTitle: false },
        width: 205,
        render: (_, record) => <Ellipsis>{record.nextRank?.name}</Ellipsis>,
      },
      {
        key: "formerRanks",
        title: t("commissions.rank.attrs.formerRanks"),
        ellipsis: { showTitle: false },
        width: 205,
        render: (_, record) => (
          <Ellipsis>{record.formerRanks.map((formerRank) => formerRank.name).join(", ")}</Ellipsis>
        ),
      },
    ];

    if (hasTopAgentCommissionsManagePermission) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 180,
        render: (_, record) => (
          <>
            <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateRank(record)} />

            <Divider orientation="vertical" />

            <Popconfirm
              title={t("affiliatePartner.helpers.deleteConfirm")}
              icon={<PopconfirmDeleteIcon />}
              cancelText={t("common.no")}
              okText={t("common.yes")}
              okType="danger"
              onConfirm={() => handleDeleteRank(record.id)}
            >
              <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
            </Popconfirm>
          </>
        ),
      });
    }

    return columns;
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Table<CommissionsSettingsRank>
      {...tableStandardProps()}
      columns={getColumns()}
      loading={isLoading}
      scroll={{ x: TableSizes.MEDIUM }}
      dataSource={data?.pageData}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
      }}
      onChange={handleTableChange}
    />
  );
};
